<template>
  <top-navigation
    previous-route="settings"
    :title="$t('pages.setting.profile_settings_title')"
  />

  <div class="mx-auto min-h-screen px-4 pt-24 pb-8">
    <!-- Default Profile Section -->
    <default-profile-editor
      v-model:name="defaultProfile.name"
      v-model:birthday="defaultProfile.birthday"
      :picture-url="defaultProfile.previewUrl || defaultProfilePictureUrl"
      :updating="defaultProfile.updating"
      :name-valid="isDefaultNameValid"
      :birthday-valid="isDefaultBirthdayValid"
      :profile-valid="isDefaultProfileValid"
      @picture-change="handleDefaultPictureChange"
      @save="saveDefaultProfile"
    />

    <!-- Divider -->
    <div class="relative my-8">
      <div class="absolute inset-0 flex items-center">
        <div
          class="w-full border-t-2 border-black-dark dark:border-slate-600"
        ></div>
      </div>
      <div class="relative flex justify-center">
        <span
          class="bg-white px-4 text-sm font-medium text-gray-600 dark:bg-kekkle-dark-dark dark:text-slate-400"
        >
          {{ $t("settings.group_profiles") }}
        </span>
      </div>
    </div>

    <!-- Group Profiles Section -->
    <div class="mb-4">
      <h2
        class="mb-3 flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-slate-100"
      >
        <span class="text-2xl">👥</span>
        {{ $t("settings.your_group_profiles") }}
      </h2>

      <information-text
        :information="$t('settings.profiles_for_each_group_explanation')"
        class="mb-6"
      />
    </div>

    <!-- Groups List with Inline Editing -->
    <div class="space-y-6">
      <template v-for="[groupId, group] in userGroupsArray" :key="groupId">
        <group-profile-editor
          v-if="groupProfiles[groupId]"
          v-model:member-name="groupProfiles[groupId].name"
          :group-name="group.name"
          :picture-url="getProfilePictureUrl(groupId)"
          :updating="groupProfiles[groupId].updating || false"
          :name-valid="isNameValid(groupId)"
          :profile-valid="isGroupProfileValid(groupId)"
          @picture-change="(file) => handleGroupPictureChange(groupId, file)"
          @save="saveGroupProfile(groupId)"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue";
import TopNavigation from "@/components/TopNavigation.vue";
import InformationText from "@/components/InformationText.vue";
import DefaultProfileEditor from "@/components/profile/DefaultProfileEditor.vue";
import GroupProfileEditor from "@/components/profile/GroupProfileEditor.vue";
import {
  setSuccessToast,
  setWarningToast,
  setErrorToast,
} from "@/helpers/notifications/toast";
import { ImageSizes } from "@/enums/images-sizes";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import type { FirestoreError } from "firebase/firestore";
import { collection, doc, updateDoc } from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import {
  uploadGroupMemberProfilePicture,
  getGroupMemberProfilePictureFromFirestorage,
} from "@/helpers/firestorage/GroupMemberPictureService";
import {
  uploadUserProfilePicture,
  getUserProfilePictureFromFirestorage,
} from "@/helpers/firestorage/UserPictureService";
import { updateUserDoc } from "@/helpers/firestore/UserService";
import { updateUserBirthdayInGroups } from "@/helpers/firestore/GroupService";
import {
  validateUserProfileData,
  validateFileSize,
  parseDateString,
  formatDateToString,
} from "@/helpers/utils/validate-user";
import { useI18n } from "vue-i18n";

const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const { t } = useI18n();

const uuid = computed(() => authStore.authUuid);

const userGroupsArray = computed(() => {
  return Array.from(groupsStore.userGroups?.entries() || []);
});

// Default Profile State
type DefaultProfile = {
  name: string;
  originalName: string;
  birthday: string;
  originalBirthday: string;
  pictureFile: File | null;
  previewUrl: string;
  updating: boolean;
};

const defaultProfile = reactive<DefaultProfile>({
  name: "",
  originalName: "",
  birthday: "",
  originalBirthday: "",
  pictureFile: null,
  previewUrl: "",
  updating: false,
});

const defaultProfilePictureUrl = computed(() => {
  const pictureData = userStore.userProfilePictureData;
  if (pictureData?.[ImageSizes.SD]?.url) {
    return pictureData[ImageSizes.SD].url;
  }
  return "";
});

const defaultValidation = computed(() => {
  return validateUserProfileData(defaultProfile.name, defaultProfile.birthday);
});

const isDefaultNameValid = computed(() => !defaultValidation.value.errors.name);
const isDefaultBirthdayValid = computed(
  () => !defaultValidation.value.errors.birthday,
);
const isDefaultProfileValid = computed(() => defaultValidation.value.isValid);

const hasDefaultNameChanged = computed(
  () => defaultProfile.name !== defaultProfile.originalName,
);
const hasDefaultBirthdayChanged = computed(
  () => defaultProfile.birthday !== defaultProfile.originalBirthday,
);
const hasDefaultProfileDataChanged = computed(
  () => hasDefaultNameChanged.value || hasDefaultBirthdayChanged.value,
);

function handleDefaultPictureChange(file: File): void {
  const maxSizeMB = 100;
  if (!validateFileSize(file, maxSizeMB)) {
    setWarningToast({
      message: t("global.toast.file_too_big", [maxSizeMB]),
    });
    return;
  }

  defaultProfile.pictureFile = file;
  defaultProfile.previewUrl = URL.createObjectURL(file);
}

async function saveDefaultProfile(): Promise<void> {
  if (!isDefaultProfileValid.value || !uuid.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    return;
  }

  defaultProfile.updating = true;

  try {
    // Upload profile picture if changed
    if (defaultProfile.pictureFile) {
      await uploadUserProfilePicture(uuid.value, defaultProfile.pictureFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 3840,
      });

      // Clear store cache
      userStore.resetUserProfilePictureData();

      // Fetch fresh image directly from Firebase, bypassing cache
      const result = await getUserProfilePictureFromFirestorage(
        uuid.value,
        ImageSizes.SD,
        true, // bypassCache = true
      );

      // Update store with fresh picture
      userStore.setUserProfilePictureData(
        {
          url: result.url,
          fromCache: result.fromCache,
        },
        ImageSizes.SD,
      );

      defaultProfile.pictureFile = null;
      defaultProfile.previewUrl = "";
    }

    // Update user profile data if changed
    if (hasDefaultProfileDataChanged.value) {
      const birthdayDate = parseDateString(defaultProfile.birthday);

      await updateUserDoc(uuid.value, {
        name: defaultProfile.name,
        ...(birthdayDate !== null && { birthday: birthdayDate }),
      });

      // Update birthday in all groups if birthday changed
      if (hasDefaultBirthdayChanged.value) {
        console.log("here");
        const userData = userStore.userData;
        if (userData && userData.groups && userData.groups.length > 0) {
          const birthdayDate = parseDateString(defaultProfile.birthday);
          const result = await updateUserBirthdayInGroups(
            uuid.value,
            userData.groups,
            birthdayDate,
          );

          if (!result.success) {
            setErrorToast({
              message: t("global.toast.birthday_not_saved_all_groups"),
            });
          }
        }
      }

      defaultProfile.originalName = defaultProfile.name;
      defaultProfile.originalBirthday = defaultProfile.birthday;
    }

    setSuccessToast({
      message: t("global.toast.profile_saved"),
    });

    await userStore.refreshUser(uuid.value);
  } catch (error) {
    console.error("Error updating default profile:", error);
    const message = handleFirestoreException({ e: error as FirestoreError });
    setErrorToast({ message });
  } finally {
    defaultProfile.updating = false;
  }
}

// Group Profiles State
type GroupProfile = {
  name: string;
  originalName: string;
  newPictureFile: File | null;
  newPictureUrl: string;
  updating: boolean;
};

const groupProfiles = reactive<Record<string, GroupProfile>>({});

function handleGroupPictureChange(groupId: string, file: File): void {
  const maxSizeMB = 100;
  if (!validateFileSize(file, maxSizeMB)) {
    setWarningToast({
      message: t("global.toast.file_too_big", [maxSizeMB]),
    });
    return;
  }

  if (groupProfiles[groupId]) {
    groupProfiles[groupId].newPictureFile = file;
    groupProfiles[groupId].newPictureUrl = URL.createObjectURL(file);
  }
}

function initializeGroupProfiles(): void {
  userGroupsArray.value.forEach(([groupId, group]) => {
    if (!uuid.value) return;
    const memberName = group.members?.[uuid.value]?.name || "";
    groupProfiles[groupId] = {
      name: memberName,
      originalName: memberName,
      newPictureFile: null,
      newPictureUrl: "",
      updating: false,
    };
  });
}

function initializeDefaultProfile(): void {
  const userData = userStore.userData;
  if (userData) {
    defaultProfile.originalName = userData.name || "";
    defaultProfile.name = userData.name || "";

    if (userData.birthday) {
      // Ensure birthday is a Date object (it might be a Timestamp from Firestore)
      const birthdayDate =
        userData.birthday instanceof Date
          ? userData.birthday
          : (userData.birthday as any).toDate?.() || userData.birthday;
      const birthdayStr = formatDateToString(birthdayDate);
      defaultProfile.originalBirthday = birthdayStr;
      defaultProfile.birthday = birthdayStr;
    }
  }
}

function getProfilePictureUrl(groupId: string): string {
  // If there's a new picture selected, show that
  if (groupProfiles[groupId]?.newPictureUrl) {
    return groupProfiles[groupId].newPictureUrl;
  }

  // Otherwise show the stored picture
  if (!uuid.value) return "";
  const pictureData =
    groupsStore.groupMembersProfilePictureData?.[groupId]?.[uuid.value]?.[
      ImageSizes.SD
    ];
  return pictureData?.url || "";
}

function isNameValid(groupId: string): boolean {
  const name = groupProfiles[groupId]?.name;
  return !!(name && name.length > 1);
}

function isGroupProfileValid(groupId: string): boolean {
  return isNameValid(groupId);
}

async function saveGroupProfile(groupId: string): Promise<void> {
  if (!uuid.value || !isGroupProfileValid(groupId) || !groupProfiles[groupId]) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    return;
  }

  const profile = groupProfiles[groupId];
  profile.updating = true;

  try {
    // Upload profile picture if changed
    if (profile.newPictureFile) {
      await uploadGroupMemberProfilePicture(
        groupId,
        uuid.value,
        profile.newPictureFile,
      );

      // Clear store cache
      groupsStore.clearGroupMemberProfilePictureData({
        uuid: uuid.value,
        groupId,
      });

      // Fetch fresh image directly from Firebase, bypassing cache
      const result = await getGroupMemberProfilePictureFromFirestorage(
        groupId,
        uuid.value,
        ImageSizes.SD,
        true, // bypassCache = true
      );
      groupsStore.setGroupMembersProfilePictureData({
        format: ImageSizes.SD,
        url: result.url,
        groupId,
        uuid: uuid.value,
        fromCache: result.fromCache,
      });

      // Clear the new picture state
      profile.newPictureFile = null;
      profile.newPictureUrl = "";
    }

    // Update name if changed
    if (profile.name !== profile.originalName) {
      const groupCollection = collection(firestore, "groups");
      const groupDocRef = doc(groupCollection, groupId);
      await updateDoc(groupDocRef, {
        [`members.${uuid.value}.name`]: profile.name,
      });

      // Update original name
      profile.originalName = profile.name;
    }

    setSuccessToast({
      message: t("global.toast.profile_saved"),
    });

    // Refresh group data
    await groupsStore.refreshAllUserGroupsData();
  } catch (error) {
    const message = handleFirestoreException({ e: error as FirestoreError });
    setErrorToast({ message });
  } finally {
    profile.updating = false;
  }
}

async function loadAllProfilePictures(): Promise<void> {
  if (!uuid.value) return;

  const loadPromises = userGroupsArray.value.map(async ([groupId]) => {
    try {
      const result = await getGroupMemberProfilePictureFromFirestorage(
        groupId,
        uuid.value!,
        ImageSizes.SD,
      );
      groupsStore.setGroupMembersProfilePictureData({
        format: ImageSizes.SD,
        url: result.url,
        groupId,
        uuid: uuid.value!,
        fromCache: result.fromCache,
      });
    } catch (error) {
      console.error(
        `Error loading profile picture for group ${groupId}:`,
        error,
      );
      // Picture will show placeholder if it fails
    }
  });

  await Promise.all(loadPromises);
}

// Watch for userData changes to re-initialize when data loads
watch(
  () => userStore.userData,
  (newUserData) => {
    if (newUserData) {
      initializeDefaultProfile();
    }
  },
  { immediate: true },
);

// Watch for groups changes to initialize group profiles
watch(
  () => groupsStore.userGroups,
  (newGroups) => {
    if (newGroups && newGroups.size > 0) {
      initializeGroupProfiles();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  // Ensure user data is loaded before initializing profiles
  if (!userStore.userData && uuid.value) {
    await userStore.refreshUser(uuid.value);
  }

  // Initialize profiles (will also be called by watcher)
  initializeDefaultProfile();
  initializeGroupProfiles();

  // Load default profile picture if not already loaded
  if (!userStore.userProfilePictureData?.[ImageSizes.SD]) {
    userStore.downloadUserProfilePictureData(ImageSizes.SD);
  }

  await loadAllProfilePictures();
});
</script>
